import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

/**
 * If a Supabase auth callback (magic link / password recovery) lands at any
 * non-portal route with `#access_token=...&type=...` in the hash, forward
 * the user into the portal so a) they actually see a useful screen and
 * b) the supabase-js `detectSessionInUrl` handler can still consume the
 * tokens (we preserve the hash on the navigate call).
 */
export function AuthHashRedirector() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const hash = location.hash || "";
    const hasToken = hash.includes("access_token=");
    const hasError = hash.includes("error=") || hash.includes("error_code=");
    if (!hasToken && !hasError) return;

    // Only intercept magic link / recovery / signup / invite callbacks
    // (or matching error responses Supabase appends to the same hash).
    const isAuthCallback =
      /type=(magiclink|recovery|signup|invite)/.test(hash) || hasError;
    if (!isAuthCallback) {
      console.debug("[AuthHashRedirector] hash present but not an auth callback", {
        pathname: location.pathname,
        hashPreview: hash.slice(0, 60),
      });
      return;
    }

    // Already on a portal route — let that page handle it.
    if (location.pathname.startsWith("/portal")) {
      console.debug("[AuthHashRedirector] already on portal route, skipping", {
        pathname: location.pathname,
      });
      return;
    }

    const callbackType =
      hash.match(/type=([a-z]+)/)?.[1] ?? (hasError ? "error" : "unknown");

    // Recovery (standard password reset) → reset-password page.
    // Magic link / invite / signup / temp-access → change-password page.
    const target =
      callbackType === "recovery"
        ? "/portal/reset-password"
        : "/portal/change-password";

    console.info("[AuthHashRedirector] forwarding auth callback to portal", {
      from: location.pathname,
      to: target,
      type: callbackType,
      hasToken,
      hasError,
    });

    navigate({ pathname: target, hash }, { replace: true });
  }, [location.pathname, location.hash, navigate]);

  return null;
}

export default AuthHashRedirector;
